import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, NumericKeypad, orderNoShort } from '@cuito/ui';
import { decodeOrderCode, type OrderCodeHeader, type OrderCodeLine } from '@cuito/codes';
import { readErrorCode } from '../../lib/apiError';
import { formatCents } from '../../lib/schemeForm';
import { resolveOrderCode } from '../api/codes';
import { resolveShortCode } from '../api/orders';
import { useCashierStream } from '../CashierLayout';
import { offlineOrderCodeExpiredAtSeconds } from '../data/offlineOrderTtl';
import { markOfflineScan } from '../offline/offlineScans';
import { readCashierLocationCode } from '../lib/prefs';

/**
 * 扫码 / 短码取单（§3.3 `/cashier/scan`、§20.7）。
 *
 * 两个通道：
 *   ① **短码**（人工）：`POST /orders/resolve-short-code`，只在下单地点有效；
 *   ② **订单码**（相机/扫码枪）：先 `decodeOrderCode` **离线**解析并渲染清单，
 *      **在线时**再 `POST /codes/resolve/order` 回源校验（404/409 按 §10.3 提示）。
 *
 * 去重只挂在相机帧上（`{ dedupe: true }`）：手输是明确的人工动作，
 * 复用去重会把第二次手输静默吞掉（Phase 4 链 E 审查必修项 ⑦）。
 *
 * ⚠️ 根锚点 `cashier-scan-page` **保留**：`routes.spec.tsx` 按它逐条验证六条路由
 * （Task 13 冻结），计划 Task 17 的实现片段漏了它 —— 丢锚点会直接红掉既有用例。
 */
export const SCAN_DEDUPE_MS = 1500;
export const SCAN_POLL_MS = 500;

/** 「无法识别」是 §3.3 三类文案里的兜底项：格式/CRC/地点不匹配都折进它 */
export const SCAN_UNRECOGNIZED_TEXT = '无法识别的订单码';
export const SCAN_NOT_FOUND_TEXT = '订单不存在';
export const SCAN_TAKEN_TEXT = '该订单已被接单';

/**
 * 离线接单的两道本地闸门文案（契约 §20.7 第 5c / 第 8 步的逐字提示）。
 *
 * ⚠️ 这两条**不是**「顺手在客户端也做一遍服务端的事」：
 *   · 第 8 步（地点一致）在没有网络时**只有**客户端判定得了 —— 服务端那道兜底要联网才到得了；
 *   · 第 5c 步（时限）读的是**同一个** `OFFLINE_ORDER_TTL_HOURS`（经 `data/offlineOrderTtl.ts`
 *     投影），因此两端的时限不可能各自漂移。
 * 文案逐字取自契约 §20.7 的表格（`该订单不属于本店` / `订单码已过期（超过 24 小时），请让顾客重新下单`）。
 */
export const SCAN_LOCATION_MISMATCH_TEXT = '该订单不属于本店';
export const SCAN_EXPIRED_TEXT = '订单码已过期（超过 24 小时），请让顾客重新下单';
/** 离线闸门 1（§10.4）的对应文案：码在传输/打印里被改过，重扫不解决就得让顾客重新下单 */
export const SCAN_CHECKSUM_FAILED_TEXT = '订单码校验失败，请重新扫描';
/** §20.7 第 11 步（恢复联网回源）的两句提示 */
export const SCAN_REVERIFY_NOT_FOUND_TEXT = '订单不存在，请核对';
export const SCAN_REVERIFY_TAKEN_TEXT = '已被接单';

/**
 * 地点还没到位时的如实提示。
 *
 * 它同时是「确认」键的**禁用理由**：地点未知时按钮禁用、这句就写在按钮旁边；
 * 地点一到，按钮启用、这句与之一同收掉（同源判据 `locationId === null`）。
 */
export const SCAN_NO_LOCATION_TEXT = '请先选择地点';

/** 后端的业务码 → §3.3 的三类文案（一处收口，页面里不再散落 if） */
export function scanErrorText(error: unknown): string {
  const code = readErrorCode(error);
  if (code === 'ORDER_ALREADY_ACCEPTED') return SCAN_TAKEN_TEXT;
  if (code === 'ORDER_SHORT_CODE_NOT_FOUND' || code === 'ORDER_NOT_FOUND' || code === 'ORDER_COMPLETED') {
    return SCAN_NOT_FOUND_TEXT;
  }
  // Phase 9：离线接单的三道闸门（§10.4）在服务端各有**可区分**的码，别再折进「无法识别」
  // —— 折进去的话店员只知道「扫不了」，不知道是「码过期了」（让顾客重新下单）还是
  // 「码被改过」（重新扫）。这两个动作完全不同，文案必须分得开。
  if (code === 'ORDER_CODE_EXPIRED') return SCAN_EXPIRED_TEXT;
  if (code === 'ORDER_CODE_LOCATION_MISMATCH') return SCAN_LOCATION_MISMATCH_TEXT;
  if (code === 'ORDER_CODE_CHECKSUM_FAILED') return SCAN_CHECKSUM_FAILED_TEXT;
  return SCAN_UNRECOGNIZED_TEXT;
}

/** 载荷的最后一个 `|` 字段就是 CRC（§20.2 的 12 字段口径） */
export function checksumOf(payload: string): string {
  const fields = payload.split('|');
  return fields[fields.length - 1] ?? '';
}

/** 取景实现（可注入：jsdom 里没有 `getUserMedia` / `BarcodeDetector`） */
export interface ScanCamera {
  supported(): boolean;
  start(input: { video: HTMLVideoElement | null; onFrame: (payload: string) => void }): Promise<{ stop: () => void }>;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue?: string }>>;
}
type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

function barcodeDetectorCtor(): BarcodeDetectorCtor | null {
  const candidate = (globalThis as { BarcodeDetector?: unknown }).BarcodeDetector;
  return typeof candidate === 'function' ? (candidate as BarcodeDetectorCtor) : null;
}

/** 真机取景：`BarcodeDetector` + `getUserMedia`，两者缺一就不可用（取景框不能是死的） */
export function browserScanCamera(pollMs: number = SCAN_POLL_MS): ScanCamera {
  return {
    supported: () =>
      barcodeDetectorCtor() !== null && typeof navigator.mediaDevices?.getUserMedia === 'function',
    async start({ video, onFrame }) {
      const media = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (video !== null) {
        video.srcObject = media;
        try {
          await video.play();
        } catch {
          /* 自动播放被策略拦下时预览是黑的，但 `detect(video)` 仍能取帧 */
        }
      }
      const Detector = barcodeDetectorCtor();
      const detector = Detector === null ? null : new Detector({ formats: ['qr_code'] });
      const timer = setInterval(() => {
        void (async () => {
          if (detector === null || video === null) return;
          const results = await detector.detect(video);
          const raw = results[0]?.rawValue;
          if (typeof raw === 'string' && raw !== '') onFrame(raw);
        })().catch(() => undefined);
      }, pollMs);
      return {
        stop: () => {
          clearInterval(timer);
          // 不释放轨道的话手机上摄像头一直亮着（iOS Safari 会持续显示「正在使用相机」）
          media.getTracks().forEach((track) => track.stop());
        },
      };
    },
  };
}

interface DecodedView {
  header: OrderCodeHeader;
  lines: OrderCodeLine[];
  payload: string;
}

interface Props {
  /** 测试注入用；缺省是真机取景 */
  camera?: ScanCamera;
}

export default function CashierScanPage({ camera }: Props) {
  const navigate = useNavigate();
  /**
   * 地点从**布局的流上下文**订阅，不在挂载时快照 `readCashierLocationId()`（Task 24 验收缺陷 3，P0）。
   *
   * 冷启动深链 `/cashier/scan?locationId=<uuid>` 的顺序是「页面先挂载、布局的 `GET /locations`
   * 后回来」：快照写法读到的是 `null` 且**再也不重读** ⇒ 手输短码连
   * `POST /orders/resolve-short-code` 都不发（页面却报「请先选择地点」，而同一时刻偏好里
   * 的地点其实已经写好了）。订阅之后，布局把地点写进上下文 ⇒ 本页自动重渲染，取单照常。
   */
  const { locationId } = useCashierStream();
  const [shortCode, setShortCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [decoded, setDecoded] = useState<DecodedView | null>(null);
  const [cameraFailed, setCameraFailed] = useState(false);
  /** 扫码枪/相机帧的人工入口（真机帧走同一个 `submitPayload`，不为测试造第二套解析） */
  const [payloadText, setPayloadText] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastScanRef = useRef<{ code: string; at: number } | null>(null);
  // ⚠️ 必须 memo：`browserScanCamera()` 每次调用都返回**新对象**，写成 `camera ?? browserScanCamera()`
  // 会让取景 effect 的依赖每次渲染都变 ⇒ 每按一位短码就重启一次摄像头（手机上画面黑一下）。
  const cameraImpl = useMemo(() => camera ?? browserScanCamera(), [camera]);

  const openOrder = useCallback(
    (orderNo: string, orderId: string): void => {
      navigate(`/cashier/orders/${orderNo}`, { state: { orderId } });
    },
    [navigate],
  );

  /**
   * 地点「后到」时把陈旧的「请先选择地点」收掉。
   *
   * 地点未知时「确认」是禁用的（点不出这条错误），所以这里清的是**留在页面上的痕迹**：
   * 地点一度为空（顶栏切地点、深链先挂载）再到回来时，页面上不许继续挂着一句已经过期的假话。
   * 只清这一条、不发任何请求。
   */
  useEffect(() => {
    if (locationId === null) return;
    setError((current) => (current === SCAN_NO_LOCATION_TEXT ? null : current));
  }, [locationId]);

  const submitShortCode = useCallback(async (): Promise<void> => {
    if (shortCode === '') {
      setError(SCAN_UNRECOGNIZED_TEXT);
      return;
    }
    // 第二道闸：按钮在地点未知时已禁用（见下面的渲染），这里保证**程序化调用**也绝不静默
    if (locationId === null) {
      setError(SCAN_NO_LOCATION_TEXT);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const order = await resolveShortCode({ shortCode, locationId });
      openOrder(order.orderNo, order.id);
    } catch (e) {
      setError(scanErrorText(e));
    } finally {
      setBusy(false);
    }
  }, [locationId, openOrder, shortCode]);

  const submitPayload = useCallback(
    async (raw: string, options: { dedupe?: boolean } = {}): Promise<void> => {
      const payload = raw.trim();
      if (payload === '') {
        setError(SCAN_UNRECOGNIZED_TEXT);
        return;
      }
      // 去重**只**给相机帧用（见文件头）
      if (options.dedupe === true) {
        const now = Date.now();
        const last = lastScanRef.current;
        if (last !== null && last.code === payload && now - last.at < SCAN_DEDUPE_MS) return;
        lastScanRef.current = { code: payload, at: now };
      }

      const result = decodeOrderCode(payload);
      if (!result.ok) {
        // 解码失败时**连请求体都构造不出来**（orderNo/shortCode/checksum 全在载荷里）
        setError(SCAN_UNRECOGNIZED_TEXT);
        return;
      }

      setError(null);

      /*
       * 契约 §20.7 的**离线两道闸门**（第 5c 步时限、第 8 步地点），只在离线时判：
       * 在线时权威判据在服务端（同一条闸门 `common/errors/order-code-gate.ts`，
       * 离线意图走 `POST /codes/resolve/order?offline=true`），客户端再判一遍只会
       * 造出第二份判据 —— 那种「两边都判」的地方，漂移的方向永远是客户端更宽松。
       *
       * 两道闸门都在 `setDecoded` **之前**：被拒的码不该在页面上留下一张「清单」，
       * 那会让店员以为单子已经取到了（§20.7 的第 9 步是「渲染挑拣清单」，第 8 步没过就到不了）。
       */
      if (navigator.onLine === false) {
        const currentLocationCode = readCashierLocationCode();
        // `null` = 本机还不知道自己在哪个店（从没成功拉过 GET /locations）⇒ **不判**：
        // 拿不到对照物就说人家不属于本店，会让一台刚装好的机器拒掉本店全部的码。
        if (currentLocationCode !== null && result.header.locationCode !== currentLocationCode) {
          setError(SCAN_LOCATION_MISMATCH_TEXT);
          setDecoded(null);
          return;
        }
        // 时限的唯一出处是 `data/offlineOrderTtl.ts`（读 `@cuito/codes` 的同一个常量）
        if (offlineOrderCodeExpiredAtSeconds(result.header.issuedAt)) {
          setError(SCAN_EXPIRED_TEXT);
          setDecoded(null);
          return;
        }
      }

      setDecoded({ header: result.header, lines: result.lines, payload: result.page.payload });

      // 离线（或没选地点）时到此为止：本地清单就是全部能力，一个请求都不发
      if (navigator.onLine === false || locationId === null) {
        // 记下「这单是离线取的」：收款确认要按它带 `origin = 'OFFLINE_SCAN'`（契约 §12.2），
        // 而那时的 `navigator.onLine` 可能已经恢复成 true 了（见 `offline/offlineScans.ts`）
        markOfflineScan(result.header.orderNo);
        setNotice('离线解析');
        return;
      }

      setBusy(true);
      try {
        const verified = await resolveOrderCode({
          orderNo: result.header.orderNo,
          shortCode: result.header.shortCode,
          locationId,
          checksum: checksumOf(result.page.payload),
        });
        openOrder(verified.order.orderNo, verified.order.id);
      } catch (e) {
        // 404 必须显著提示（REQ-CASHIER-024），本地记录保留在页面上
        setError(scanErrorText(e));
      } finally {
        setBusy(false);
      }
    },
    [locationId, openOrder],
  );

  const submitRef = useRef(submitPayload);
  useEffect(() => {
    submitRef.current = submitPayload;
  }, [submitPayload]);

  /**
   * 契约 §20.7 第 11 步：**恢复联网后必须回源校验**（`REQ-CASHIER-024`）。
   *
   * 离线解码出来的清单是「顾客手机上的那张码」的投影，而**只有服务端知道**这单有没有被
   * 别人接走、有没有被取消。回到有网的那一刻不校验，店员就会照着本地清单挑货、收款，
   * 而服务端那边这张单可能已经是别人的了 —— 那正是离线流程最贵的一种错。
   *
   * 只**提示**、不改页面上的清单：本机的记录留着供对账（§18.2 对冲突项的同一口径），
   * 真正的拦截在「接单」那一步（服务端 409）。
   */
  const reverify = useCallback(async (): Promise<void> => {
    if (decoded === null || locationId === null) return;
    try {
      await resolveOrderCode({
        orderNo: decoded.header.orderNo,
        shortCode: decoded.header.shortCode,
        locationId,
        checksum: checksumOf(decoded.payload),
      });
    } catch (e) {
      const code = readErrorCode(e);
      if (code === 'ORDER_ALREADY_ACCEPTED') setNotice(SCAN_REVERIFY_TAKEN_TEXT);
      else if (code === 'ORDER_NOT_FOUND' || code === 'ORDER_COMPLETED' || code === 'ORDER_SHORT_CODE_NOT_FOUND') {
        setNotice(SCAN_REVERIFY_NOT_FOUND_TEXT);
      }
    }
  }, [decoded, locationId]);

  useEffect(() => {
    const onOnline = (): void => {
      void reverify();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [reverify]);

  /**
   * 取景轮询。`submitRef` 是必需的：`submitPayload` 依赖短码/地点（每次输入都会换引用），
   * 直接进依赖数组会让**每输入一位就重启一次摄像头**（手机上表现为画面黑一下、
   * 并反复弹「允许使用相机」）。所以 effect 只在「取景实现」变化时重跑。
   */
  useEffect(() => {
    if (!cameraImpl.supported()) return;
    let cancelled = false;
    let handle: { stop: () => void } | null = null;

    void cameraImpl
      .start({
        video: videoRef.current,
        onFrame: (frame) => {
          void submitRef.current(frame, { dedupe: true });
        },
      })
      .then((started) => {
        if (cancelled) {
          started.stop();
          return;
        }
        handle = started;
      })
      .catch(() => {
        if (!cancelled) setCameraFailed(true);
      });

    return () => {
      cancelled = true;
      handle?.stop();
    };
  }, [cameraImpl]);

  const cameraAvailable = cameraImpl.supported() && !cameraFailed;

  return (
    <div data-testid="cashier-scan-page" className="flex flex-col gap-3">
      <div
        data-testid="scan-frame"
        className="relative aspect-square w-full max-w-sm overflow-hidden rounded-lg border-2 border-black bg-gray-100"
      >
        {cameraAvailable ? (
          <>
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
            <span className="absolute inset-x-0 bottom-0 bg-black/70 px-2 py-1 text-center text-sm font-bold text-white">
              对准顾客手机上的订单码
            </span>
          </>
        ) : (
          <p className="flex h-full items-center justify-center px-4 text-center text-sm text-gray-500">
            本机不支持相机取景，请用手输短码
          </p>
        )}
      </div>

      {error !== null && (
        <p data-testid="scan-error" role="alert" className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm font-bold text-red-600">
          {error}
        </p>
      )}
      {notice !== null && (
        <p data-testid="scan-notice" className="rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">
          {notice}
        </p>
      )}

      <section data-testid="manual-short-code" className="flex flex-col gap-2">
        <p className="text-sm font-bold">手输短码（4–6 位）</p>
        <output data-testid="short-code-value" className="text-2xl font-bold tracking-widest">
          {shortCode === '' ? '—' : shortCode}
        </output>
        <NumericKeypad value={shortCode} onChange={setShortCode} maxLength={6} />
        {/*
         * 地点未知 ⇒ **禁用**，并且把理由写在按钮旁边。
         *
         * 「能点但不发请求」在店员眼里就是一次点击被静默吞掉（Task 24 验收现场：提示已被清掉、
         * `POST /orders/resolve-short-code` 也没发）。禁用与理由同源（都看 `locationId === null`），
         * 所以地点一到就同时翻转：按钮启用、理由消失；**已敲的短码留在 `shortCode` 里不动**。
         */}
        {locationId === null && (
          <p
            data-testid="scan-no-location"
            className="rounded-lg border-2 border-gray-400 bg-gray-50 p-2 text-sm font-bold text-gray-600"
          >
            {SCAN_NO_LOCATION_TEXT}
          </p>
        )}
        <Button
          data-testid="scan-confirm"
          onClick={() => void submitShortCode()}
          disabled={busy || locationId === null}
        >
          {busy ? '取单中…' : '确认'}
        </Button>
      </section>

      {/*
       * 扫码枪 / 相机帧的人工入口：与相机帧共用同一个 `submitPayload`
       * （两条入口两套解析逻辑的话，测出来的就不是线上那条路）。
       */}
      <section className="flex flex-wrap items-end gap-2">
        <Input
          label="订单码载荷（扫码枪 / 相机帧）"
          className="w-72"
          data-testid="scan-payload-input"
          value={payloadText}
          onChange={(event) => setPayloadText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            const text = payloadText;
            setPayloadText('');
            void submitPayload(text);
          }}
        />
        <Button
          data-testid="scan-payload-confirm"
          variant="secondary"
          onClick={() => {
            const text = payloadText;
            setPayloadText('');
            void submitPayload(text);
          }}
        >
          解析订单码
        </Button>
      </section>

      {decoded !== null && (
        <section data-testid="scan-decoded" className="flex flex-col gap-2 rounded-lg border-2 border-black p-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* 前 8 位（§7.8，Phase 5 Task 22 统一到 `orderNoShort`；本处原为后 4 位） */}
            <span className="text-base font-bold">订单 {orderNoShort(decoded.header.orderNo)}</span>
            <span className="text-base font-bold">短码 {decoded.header.shortCode}</span>
            {notice === '离线解析' && (
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">离线解析</span>
            )}
            <span data-testid="scan-decoded-total" className="ml-auto text-xl font-bold">
              {formatCents(decoded.header.totalAmountCents)}
            </span>
          </div>
          <ul className="flex flex-col gap-1">
            {decoded.lines.map((entry) => (
              <li
                key={entry.lineNo}
                data-testid="scan-decoded-line"
                className="flex items-center justify-between rounded-lg border-2 border-black p-2 text-sm"
              >
                <span className="flex items-center gap-2">
                  <span>{entry.productCode}</span>
                  {entry.inPromotion && (
                    <span data-testid="scheme-badge" className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
                      组合促销
                    </span>
                  )}
                </span>
                <span className="text-xs text-gray-500">货架 {entry.shelfCode}</span>
                <span>×{entry.qty}</span>
                <span className="font-bold">{formatCents(entry.unitPriceCents)}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            离线解析只显示商品码与数量，商品名与顾客信息以在线回源结果为准（§20.7 步骤 9）。
          </p>
        </section>
      )}
    </div>
  );
}
